import "./PaneLayout.inline.css";
import "./PaneLayout.css";
import { PaneHeader } from "../PaneHeader/PaneHeader";
import type { Pane, PanesManager } from "../../managers/PanesManager";
import type { SeedBibleState } from "../../managers/SeedBibleStateManager";
import { UI_SIZE_SCALE_MAP } from "../../managers/SettingsManager";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { useSignal } from "@preact/signals";

interface DragState {
  mode: "move" | "resize";
  paneId: string;
  startX: number;
  startY: number;
  placement: Pane["placement"];
  /**
   * Geometry captured at drag start, used to clamp a floating pane within the
   * viewport against its *actual* rendered box. This keeps the math correct
   * regardless of the UI `zoom` or the shell's positioned ancestor:
   * - grabOffset: pointer position relative to the pane's top-left (px)
   * - paneWidthPx/paneHeightPx: rendered footprint in real viewport px
   * - originX/originY, scaleX/scaleY: the linear map from the pane's CSS
   *   coordinate (left/top) to real viewport px, sampled from the element.
   */
  grabOffsetX?: number;
  grabOffsetY?: number;
  paneWidthPx?: number;
  paneHeightPx?: number;
  originX?: number;
  originY?: number;
  scaleX?: number;
  scaleY?: number;
}

/**
 * Closes a pane via its header's close (X) button, honoring `confirmClose`
 * if the pane declared one — e.g. to warn about unsaved changes and let the
 * pane close itself once the user confirms. See `Pane.confirmClose`.
 */
function closePaneFromHeader(panesManager: PanesManager, pane: Pane) {
  if (pane.confirmClose && !pane.confirmClose()) {
    return;
  }
  panesManager.closePane(pane.id, "user");
}

function usePaneDrag(state: SeedBibleState) {
  const dragStateRef = useRef<DragState | null>(null);
  const paneElementMapRef = useRef(new Map<string, HTMLElement>());
  const { panes: panesManager, app } = state;

  const getUiScale = () =>
    UI_SIZE_SCALE_MAP[state.settings.settings.value.uiSize];

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState) {
        return;
      }

      const deltaX =
        dragState.placement === "side"
          ? dragState.startX - event.clientX
          : event.clientX - dragState.startX;
      const deltaY = event.clientY - dragState.startY;

      if (dragState.mode === "move") {
        const grabOffsetX = dragState.grabOffsetX ?? 0;
        const grabOffsetY = dragState.grabOffsetY ?? 0;
        const paneWidthPx = dragState.paneWidthPx ?? 0;
        const paneHeightPx = dragState.paneHeightPx ?? 0;
        const scaleX = dragState.scaleX || 1;
        const scaleY = dragState.scaleY || 1;
        const originX = dragState.originX ?? 0;
        const originY = dragState.originY ?? 0;

        const maxLeft = Math.max(0, window.innerWidth - paneWidthPx);
        const maxTop = Math.max(0, window.innerHeight - paneHeightPx);
        const viewportLeft = Math.min(
          Math.max(0, event.clientX - grabOffsetX),
          maxLeft
        );
        const viewportTop = Math.min(
          Math.max(0, event.clientY - grabOffsetY),
          maxTop
        );

        panesManager.setPanePosition(
          dragState.paneId,
          (viewportLeft - originX) / scaleX,
          (viewportTop - originY) / scaleY
        );
      } else {
        panesManager.resizePane(dragState.paneId, deltaX, deltaY, getUiScale());
      }

      dragStateRef.current = {
        ...dragState,
        startX: event.clientX,
        startY: event.clientY,
      };
    };

    const handlePointerUp = () => {
      dragStateRef.current = null;
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [panesManager]);

  const startMove = (pane: Pane, event: PointerEvent) => {
    if (pane.placement !== "floating") {
      return;
    }
    event.stopPropagation();
    app.selectPane(pane.id);

    const element = paneElementMapRef.current.get(pane.id);
    const rect = element?.getBoundingClientRect();
    const scaleX = rect && pane.width ? rect.width / pane.width : 1;
    const scaleY = rect && pane.height ? rect.height / pane.height : 1;

    dragStateRef.current = {
      mode: "move",
      paneId: pane.id,
      placement: pane.placement,
      startX: event.clientX,
      startY: event.clientY,
      grabOffsetX: rect ? event.clientX - rect.left : 0,
      grabOffsetY: rect ? event.clientY - rect.top : 0,
      paneWidthPx: rect?.width ?? pane.width,
      paneHeightPx: rect?.height ?? pane.height,
      scaleX,
      scaleY,
      originX: rect ? rect.left - pane.x * scaleX : 0,
      originY: rect ? rect.top - pane.y * scaleY : 0,
    };
  };

  const startResize = (pane: Pane, event: PointerEvent) => {
    event.stopPropagation();
    event.preventDefault();
    app.selectPane(pane.id);
    dragStateRef.current = {
      mode: "resize",
      paneId: pane.id,
      placement: pane.placement,
      startX: event.clientX,
      startY: event.clientY,
    };
  };

  const registerPaneElement = (paneId: string, element: HTMLElement | null) => {
    if (element) {
      paneElementMapRef.current.set(paneId, element);
    } else {
      paneElementMapRef.current.delete(paneId);
    }
  };

  return { startMove, startResize, registerPaneElement };
}

interface PaneLayoutProps {
  state: SeedBibleState;
}

/**
 * Renders floating panes as overlays on top of the tabs layout. Fullscreen
 * panes render inside the content row via `FullscreenPane`, and side panes via
 * `SidePane` — both as normal flex children that fit the reader area instead of
 * covering the whole viewport, so the desktop sidebar stays visible.
 */
export function PaneLayout(props: PaneLayoutProps) {
  const { state } = props;

  const { app, panes: panesManager } = state;
  const overlayPanes = app.effectivePanes.value.filter(
    (pane) => pane.placement === "floating"
  );
  const selectedPaneId = panesManager.selectedPaneId.value;
  const { startMove, startResize, registerPaneElement } = usePaneDrag(state);

  return (
    <>
      {overlayPanes.map((pane, index) => (
        <div
          key={pane.id}
          className={`sb-pane-shell sb-pane-shell-detached${
            pane.id === selectedPaneId ? " sb-pane-shell-active" : ""
          }`}
          data-placement={pane.placement}
          style={{
            left: `${pane.x}px`,
            top: `${pane.y}px`,
            width: `${pane.width}px`,
            height: `${pane.height}px`,
            zIndex:
              pane.id === selectedPaneId
                ? 170 + overlayPanes.length
                : 150 + index,
          }}
          ref={(element: HTMLElement | null) =>
            registerPaneElement(pane.id, element)
          }
          onPointerDown={() => app.selectPane(pane.id)}
        >
          <PaneHeader
            title={pane.title}
            icon={pane.icon}
            leading={pane.leading}
            header={pane.header}
            onClose={() => closePaneFromHeader(panesManager, pane)}
            onPointerDown={(event: PointerEvent) => startMove(pane, event)}
          />
          <div className="sb-pane-detached-body">
            <div className="sb-pane-component">
              <pane.component />
            </div>
            <div
              className="sb-pane-detached-resize-handle"
              onPointerDown={(event: PointerEvent) => startResize(pane, event)}
            ></div>
          </div>
        </div>
      ))}
    </>
  );
}

interface FullscreenPaneProps {
  state: SeedBibleState;
  pane: Pane;
}

/**
 * Renders the single fullscreen pane (if any) inside the content row, so it
 * fills only the reader area rather than the whole app.
 *
 * On desktop this leaves the sidebar docked beside it, visible and
 * interactable. On mobile the sidebar is a hidden drawer, so the content row
 * spans the whole viewport and the pane fills the screen as before — with the
 * bottom toolbar still visible below it (BibleReaderToolbar keeps the bottom bar
 * on screen and suspends the taller verse toolbar; PaneLayout.css reserves the
 * bottom bar's height).
 *
 * All of the pane's geometry (`position`, `inset`, `z-index`) lives in
 * PaneLayout.css rather than an inline style, so the mobile bottom inset there
 * can override it.
 *
 * Only one pane can fill the screen at a time — opening a fullscreen pane
 * closes every other pane (see PanesManager) — so there is never a side or
 * floating pane sitting alongside it.
 */
export function FullscreenPane(props: FullscreenPaneProps) {
  const { state, pane } = props;
  const { app, panes: panesManager } = state;

  // `bottom` is deliberately absent from the inline style below and left to the
  // stylesheet: PaneLayout.css pins it to 0 on desktop but insets it above the
  // mobile reader toolbar, and an inline value wins over that media query —
  // which is why the mobile inset silently never applied and pane footers (the
  // plan detail's "Read" button, the plan wizard's "Next") sat underneath the
  // toolbar.
  return (
    <div
      className="sb-pane-shell sb-pane-shell-detached"
      data-placement="fullscreen"
      style={{
        position: "absolute",
        top: "0px",
        left: "0px",
        right: "0px",
        zIndex: 35,
      }}
      onPointerDown={() => app.selectPane(pane.id)}
    >
      {pane.showHeader && (
        <PaneHeader
          title={pane.title}
          icon={pane.icon}
          leading={pane.leading}
          header={pane.header}
          onClose={() => closePaneFromHeader(panesManager, pane)}
        />
      )}
      <div className="sb-pane-detached-body">
        <div className="sb-pane-component">
          <pane.component />
        </div>
      </div>
    </div>
  );
}

interface SidePaneProps {
  state: SeedBibleState;
  pane: Pane;
}

/** How long a docked pane takes to slide in or out; matches PaneLayout.css. */
const SIDE_PANE_SLIDE_MS = 250;

/**
 * How soon after a click or key press a pane change still counts as the
 * user's. The browser's own user activation lasts several seconds, long
 * enough to catch panes the app swaps on its own after an unrelated click.
 */
const USER_INPUT_WINDOW_MS = 1000;

let lastUserInputAt = -Infinity;
if (typeof window !== "undefined") {
  const rememberUserInput = () => {
    lastUserInputAt = Date.now();
  };
  for (const type of ["pointerdown", "click", "keydown"]) {
    window.addEventListener(type, rememberUserInput, {
      capture: true,
      passive: true,
    });
  }
}

/**
 * Whether a docked pane opening or closing right now should slide. Only just
 * after the user clicked or pressed a key, so a pane the app opens or closes
 * on its own, such as on load, just appears or goes. Never with reduced
 * motion.
 */
function shouldSlideSidePane(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) {
    return false;
  }
  return Date.now() - lastUserInputAt < USER_INPUT_WINDOW_MS;
}

/**
 * Slides a closing pane out. The pane itself is already gone by the time it
 * would animate, so this slides a copy of how it looked: its elements cloned
 * into the ghost host beside it (see app/main.tsx), with nothing behind them.
 * Called while the pane is still on screen, just before it's removed.
 */
function slideOutCopyOf(shell: HTMLElement) {
  const host = shell.parentElement?.querySelector<HTMLElement>(
    ":scope > .sb-pane-side-ghost-host"
  );
  if (!host) return;

  const copy = shell.cloneNode(true) as HTMLElement;
  copy.removeAttribute("data-placement");
  copy.style.setProperty("--sb-pane-side-width", `${shell.offsetWidth}px`);
  copy.classList.remove("sb-pane-side-shell-entering");
  copy.classList.add("sb-pane-side-shell-leaving");
  copy.setAttribute("inert", "");
  copy.setAttribute("aria-hidden", "true");

  // A clone starts scrolled to the top; each scrolled list is put back where
  // it was, or the copy jumps as it leaves. Paired up by position now, while
  // the copy still matches the original element for element.
  const originals = shell.querySelectorAll<HTMLElement>("*");
  const copies = copy.querySelectorAll<HTMLElement>("*");
  const scrolled: [HTMLElement, number, number][] = [];
  originals.forEach((element, index) => {
    if (element.scrollTop || element.scrollLeft) {
      scrolled.push([copies[index]!, element.scrollTop, element.scrollLeft]);
    }
  });

  // Before the copy joins the page: ids would clash with the real ones,
  // canvases copy blank, and frames and media would start loading again.
  copy
    .querySelectorAll("[id]")
    .forEach((element) => element.removeAttribute("id"));
  copy
    .querySelectorAll("canvas, video, audio, iframe")
    .forEach((element) => element.remove());

  host.appendChild(copy);
  for (const [element, top, left] of scrolled) {
    element.scrollTop = top;
    element.scrollLeft = left;
  }

  setTimeout(() => copy.remove(), SIDE_PANE_SLIDE_MS);
}

/**
 * Renders the single side pane (if any) as a normal flex child in the
 * content row (see app/main.tsx), so it pushes the tabs layout instead of
 * overlaying it.
 */
export function SidePane(props: SidePaneProps) {
  const { state, pane } = props;
  const { app, panes: panesManager } = state;
  const { startResize, registerPaneElement } = usePaneDrag(state);

  // Opening slides the width in from nothing; switching to another side pane
  // reuses this one and doesn't. Closing slides out a copy (see above).
  const shellRef = useRef<HTMLElement | null>(null);
  const [slidesIn] = useState(shouldSlideSidePane);
  const isEntering = useSignal(slidesIn);
  useEffect(() => {
    if (!isEntering.peek()) return;
    const timer = setTimeout(() => {
      isEntering.value = false;
    }, SIDE_PANE_SLIDE_MS);
    return () => clearTimeout(timer);
  }, []);
  // A layout effect's cleanup runs before the pane leaves the page, while
  // there's still something to copy.
  useLayoutEffect(
    () => () => {
      if (shellRef.current && shouldSlideSidePane()) {
        slideOutCopyOf(shellRef.current);
      }
    },
    []
  );

  return (
    <div
      className={`sb-pane-side-shell${isEntering.value ? " sb-pane-side-shell-entering" : ""}`}
      data-placement="side"
      style={{
        width: `${pane.width}px`,
        "--sb-pane-side-width": `${pane.width}px`,
      }}
      ref={(element: HTMLElement | null) => {
        shellRef.current = element;
        registerPaneElement(pane.id, element);
      }}
      onPointerDown={() => app.selectPane(pane.id)}
    >
      {pane.showHeader && (
        <PaneHeader
          title={pane.title}
          icon={pane.icon}
          leading={pane.leading}
          header={pane.header}
          onClose={() => closePaneFromHeader(panesManager, pane)}
        />
      )}
      <div className="sb-pane-detached-body">
        <div className="sb-pane-component">
          <pane.component />
        </div>
      </div>
      {/* Invisible full-height grab strip along the pane's resize edge. It's a
          direct child of the shell (not the scrolling body) so it spans the
          whole side and isn't clipped. */}
      <div
        className="sb-pane-detached-resize-handle-side"
        onPointerDown={(event: PointerEvent) => startResize(pane, event)}
      ></div>
    </div>
  );
}
